/**
 * content.js — Schema-driven landing-page content for both languages.
 * Imports raw translations from i18n.js and shapes them into block-ready props.
 * CTA labels follow the escalating dramaturgy curve, NOT the same label everywhere.
 */
import { translations as baseTranslations, company as baseCompany, media as baseMedia } from './i18n';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * Deep-merge `b` onto `a`, returning a new value. Arrays in `b` replace
 * arrays in `a` (we intentionally do NOT concat — admin must be able to
 * shorten lists). Plain objects merge recursively. Anything else in `b`
 * wins outright.
 */
function deepMerge(a, b) {
  if (!isPlainObject(a) || !isPlainObject(b)) return b ?? a;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    const av = a[k];
    const bv = b[k];
    out[k] = isPlainObject(av) && isPlainObject(bv) ? deepMerge(av, bv) : bv;
  }
  return out;
}

const objectOverlay = (value) => (isPlainObject(value) ? value : null);
const telHref = (raw = '') => 'tel:' + String(raw || '').replace(/[^+\d]/g, '');

export function getContent(lang, overlay = null) {
  // Merge build-time defaults with optional runtime overlay (from
  // `/api/content`). Overlay is a sparse `{translations, company, media}`
  // doc; missing keys fall through to the imports above.
  const translationOverlay = objectOverlay(overlay?.translations);
  const companyOverlay = objectOverlay(overlay?.company);
  const mediaOverlay = objectOverlay(overlay?.media);

  const translations = translationOverlay
    ? { nl: deepMerge(baseTranslations.nl, objectOverlay(translationOverlay.nl) || {}),
        de: deepMerge(baseTranslations.de, objectOverlay(translationOverlay.de) || {}) }
    : baseTranslations;
  const company = companyOverlay ? deepMerge(baseCompany, companyOverlay) : baseCompany;
  const media   = mediaOverlay   ? deepMerge(baseMedia,   mediaOverlay)   : baseMedia;

  // waBase is defined inside getContent so it can close over the (possibly
  // overlaid) `company.whatsapp` number.
  const waBase = (l, msg) => `https://wa.me/${company.whatsapp || baseCompany.whatsapp || ''}?text=${encodeURIComponent(msg)}`;

  const t = translations[lang] || baseTranslations[lang] || baseTranslations.nl;
  const isDE = lang === 'de';
  const localPhone = isDE
    ? (company.phoneDE || baseCompany.phoneDE || '')
    : (company.phoneNL || baseCompany.phoneNL || '');

  // Centralised escalating CTA labels — each tier of the page asks differently
  const ctaLabels = {
    hero_primary:   isDE ? 'Angebot anfragen'     : 'Offerte aanvragen',
    hero_whatsapp:  isDE ? 'Beratung via WhatsApp': 'Advies via WhatsApp',
    problem_cta:    isDE ? 'Beratung anfragen'    : 'Vraag advies aan',
    products_cta:   isDE ? 'Meine Variante zeigen': 'Bekijk uw type',
    werkwijze_cta:  isDE ? 'Messtermin vereinbaren': 'Plan een meting',
    compare_cta:    isDE ? 'Meine Fenster vergleichen': 'Vergelijk uw raam',
    form_submit:    isDE ? 'Senden'               : 'Verstuur',
    final_primary:  isDE ? 'Jetzt Angebot anfragen': 'Vraag nu uw offerte aan',
    final_phone:    isDE ? 'Anrufen'              : 'Bel ons',
    final_whatsapp: isDE ? 'WhatsApp uns'         : 'App ons',
  };

  return {
    raw: t,
    company,
    media,
    lang,
    isDE,
    helpers: { wa: (msg) => waBase(lang, msg), tel: telHref },

    nav: t.nav,

    // ===== Act I — Promise =====
    hero: {
      variant: 'promise',
      tone: 'cream',
      rhythm: 'none', // bespoke padding inside HeroBlock
      eyebrow: t.hero.mini,
      h1: [t.hero.title1, t.hero.title2],
      sub: t.hero.sub,
      mediaSrc: media.hero,
      mediaFallback: media.heroFallback,
      trustStrip: t.hero.trustStrip,
      scrollHint: t.hero.scrollHint,
      ctas: [
        { kind: 'primary', label: ctaLabels.hero_primary, href: '#cta-block', source: 'hero', testId: 'hero-cta-primary' },
        { kind: 'whatsapp', label: ctaLabels.hero_whatsapp,
          href: waBase(lang, isDE ? 'Hallo, ich interessiere mich für Insektenschutz.' : 'Hallo, ik wil graag advies over horren.'),
          source: 'hero-wa', testId: 'hero-cta-whatsapp' },
        { kind: 'phone', label: t.hero.ctaPhone, number: localPhone, href: telHref(localPhone), testId: 'hero-cta-phone' },
      ],
    },

    // ===== Act II — Confirmation =====
    trustBar: {
      tone: 'leaf',
      rhythm: 'tight',
      items: t.trustbar,
    },
    benefits: {
      id: 'voordelen',
      testId: 'benefits-section',
      tone: 'cream',
      rhythm: 'relaxed',
      ...t.benefits,
    },

    // ===== Act III — Tension =====
    problem: {
      tone: 'problem',
      rhythm: 'grand',
      side: 'right',
      eyebrow: t.problem.eyebrow,
      headline: t.problem.title,
      body: t.problem.body,
      cta: { label: ctaLabels.problem_cta, href: '#cta-block', source: 'problem', testId: 'problem-cta' },
      mediaSrc: media.product_ramen,
    },

    // ===== Act IV — Empathy =====
    empathy: {
      tone: 'cream',
      rhythm: 'grand',
      side: 'left',
      eyebrow: t.empathy.eyebrow,
      headline: t.empathy.title,
      headlineAccent: t.empathy.titleAfter,
      body: t.empathy.body,
      mediaSrc: media.empathy,
    },

    // ===== Act V — Promise + Solution =====
    promise: { ...t.promise, tone: 'sand', rhythm: 'relaxed', mediaSrc: media.promise },
    products: {
      id: 'collectie',
      testId: 'products-section',
      tone: 'cream',
      rhythm: 'relaxed',
      eyebrow: t.products.eyebrow,
      title: t.products.title,
      variant: '3-up',
      items: t.products.items.map((p) => ({
        key: p.key,
        name: p.name,
        badge: p.badge,
        price: p.price,
        scene: p.scene,
        desc: p.desc,
        // Per-card deep-link to the category landing page.
        href: ({ ramen: '/ramen', deuren: '/deuren', lichtschachten: '/lichtschachten' })[p.key],
        ctaLabel: ({
          ramen:          isDE ? 'Fenster ansehen'       : 'Bekijk raamhorren',
          deuren:         isDE ? 'Türen ansehen'         : 'Bekijk deurhorren',
          lichtschachten: isDE ? 'Lichtschächte ansehen' : 'Bekijk lichtschachten',
        })[p.key],
        image: ({
          ramen: media.product_ramen,
          deuren: media.product_deuren,
          lichtschachten: media.product_lichtschachten,
        })[p.key],
        imageHover: ({
          ramen: media.hero,
          deuren: media.empathy,
          lichtschachten: media.promise,
        })[p.key],
      })),
      cta: { label: ctaLabels.products_cta, href: '#cta-block', source: 'products', testId: 'products-cta' },
    },
    typeCompare: {
      id: 'vergelijk',
      testId: 'type-compare',
      tone: 'sand',
      rhythm: 'grand',
      eyebrow: t.typeCompare.eyebrow,
      title: t.typeCompare.title,
      sub: t.typeCompare.sub,
      items: t.typeCompare.items.map((it) => ({
        ...it,
        image: it.key === 'spann' ? media.comparisonB : media.product_ramen,
      })),
      ctaLabel: t.typeCompare.ctaLabel,
      ctaHref: '#cta-block',
      ctaTestId: 'type-compare-cta',
      ctaSource: 'type-compare',
      ctaHint: t.typeCompare.ctaHint,
      forWhoLabel: isDE ? 'Für wen?' : 'Voor wie?',
    },
    werkwijze: {
      id: 'werkwijze',
      testId: 'werkwijze-section',
      tone: 'sand',
      rhythm: 'relaxed',
      eyebrow: t.werkwijze.eyebrow,
      title: t.werkwijze.title,
      items: t.werkwijze.steps.map((s, i) => ({
        ...s,
        image: [
          '/medienpool/beratung-zu-massgeschneiderten-fliegengi-87a17660.webp',
          '/medienpool/aufmass-fliegengitter-schiebetuer-tueroe-f350aeb8.webp',
          '/medienpool/fliegengitter-spannrahmen-montage-insekt-db8fb01a.webp',
          '/medienpool/insektenschutz-plissee-schiebetuer-monta-38929bc6.webp',
        ][i],
      })),
      cta: { label: ctaLabels.werkwijze_cta, href: '#cta-block', source: 'werkwijze', testId: 'werkwijze-cta' },
    },

    // ===== Act VI — Proof =====
    bigQuote: {
      id: 'getuigenis',
      testId: 'big-quote',
      tone: 'sand',
      rhythm: 'grand',
      ...t.bigQuote,
    },
    about: {
      id: 'over-ons',
      testId: 'about-section',
      tone: 'cream',
      rhythm: 'grand',
      mediaSrc: media.empathy,
      ...t.about,
      cta: { label: t.about.ctaLabel, href: '#cta-block', source: 'about', testId: 'about-cta' },
    },
    sample: {
      id: 'gratis-advies',
      testId: 'sample-promo',
      tone: 'cream',
      rhythm: 'relaxed',
      eyebrow: t.sample.eyebrow,
      title: t.sample.title,
      body: t.sample.body,
      mediaSrc: media.promise,
      ctas: [
        { kind: 'primary', label: t.sample.ctaPrimary, href: '#cta-block', source: 'sample-primary', testId: 'sample-primary' },
        { kind: 'whatsapp', label: t.sample.ctaSecondary,
          href: waBase(lang, isDE ? 'Hallo, ich hätte gerne ein kostenloses Beratungsgespräch.' : 'Hallo, ik wil graag een gratis adviesgesprek.'),
          source: 'sample-whatsapp', testId: 'sample-whatsapp' },
      ],
    },
    sampleKit: {
      id: 'gratis-stalen',
      testId: 'sample-kit',
      tone: 'sand',
      rhythm: 'relaxed',
      imageSrc: media.product_ramen,
      ...t.sampleKit,
    },
    advisor: {
      ...t.advisor,
      waHref: waBase(lang, t.advisor.waMessage),
    },
    comparison: {
      ...t.comparison,
      tone: 'cream',
      rhythm: 'relaxed',
      cta: { label: ctaLabels.compare_cta, href: '#cta-block', source: 'comparison', testId: 'compare-cta' },
    },
    reviews: { ...t.reviews, tone: 'sand', rhythm: 'relaxed' },
    trustDeep: { ...t.trust, tone: 'cream', rhythm: 'relaxed' },

    // ===== Lifestyle / cinematic interlude (clip-shade inspired) =====
    lifestyle: {
      testId: 'lifestyle-block',
      mediaSrc: media.cta,
      ...t.lifestyle,
      cta: { label: t.lifestyle.ctaLabel, href: '#cta-block', source: 'lifestyle', testId: 'lifestyle-cta' },
    },

    // ===== Act VII — Decision =====
    leadForm: {
      tone: 'sand',
      rhythm: 'grand',
      eyebrow: t.ctaBlock.eyebrow,
      headline: t.ctaBlock.title,
      sub: t.ctaBlock.sub,
      fields: t.contact,
      submitLabel: ctaLabels.form_submit,
      success: t.ctaBlock.success,
      error: t.ctaBlock.error,
      alternates: [
        { kind: 'whatsapp', label: t.ctaBlock.whatsappAlt,
          href: waBase(lang, isDE ? 'Hallo, ich möchte ein Angebot.' : 'Hallo, ik wil graag een offerte.') },
        { kind: 'phone', label: localPhone, href: telHref(localPhone) },
      ],
    },

    // ===== Act VIII — Reassurance =====
    configurator: { ...t.configurator, tone: 'cream', rhythm: 'relaxed' },
    faq: { ...t.faq, tone: 'sand', rhythm: 'relaxed' },
    finalCta: {
      tone: 'cream-sand',
      rhythm: 'grand',
      eyebrow: t.finalCta.eyebrow,
      headline: t.finalCta.title,
      sub: t.finalCta.sub,
      bgImage: media.cta,
      ctas: [
        { kind: 'primary', label: ctaLabels.final_primary, href: '#cta-block', source: 'final-cta', testId: 'final-primary' },
        { kind: 'phone', label: ctaLabels.final_phone, href: telHref(localPhone), testId: 'final-tel' },
        { kind: 'whatsapp', label: ctaLabels.final_whatsapp,
          href: waBase(lang, isDE ? 'Hallo, ich möchte ein Angebot.' : 'Hallo, ik wil graag een offerte.'),
          testId: 'final-whatsapp' },
      ],
    },

    footer: t.footer,
  };
}
